import type { Response } from '~/types/api/v2'

import type { WorkspaceIdentifier } from '../../types'

import { useFetchWithDefaults } from '../../useFetchWithDefaults'

export type ContractParty =
  Omit<App.Data.Payloads.Contracts.ContractPartyPayload, 'order'> & {
    id: string
  }

// TODO: Move to API types
export interface SignatureParty {
  reference_id: string
  first_name: string
  last_name: string
  email: Maybe<string>
  phone: Maybe<string>
  job_title: Maybe<string>
  company_name: Maybe<string>
}

export type GuestInvitationPayload = App.Data.Payloads.Contracts.Builder.StoreContractBuilderGuestPayload

export type ResendGuestInvitationPayload = App.Data.Payloads.Contracts.Builder.ResendContractBuilderGuestPayload

// TODO: Move to API types
export interface FinalizePayload {
  signature_method: string
  send_invitations: boolean
  parties: (App.Data.Payloads.Contracts.ContractPartyPayload & { reference_id: string })[]
  signing_settings?: App.Data.Payloads.Contracts.SigningSettingsPayload
}

interface ContractBuilderRequestParams extends WorkspaceIdentifier {
  contractId: string
}

export const useFetchContractBuilder = () => {
  const { get, post, deleteRequest } = useFetchWithDefaults()
  const url = (contractId: string) => `/v2/contracts/${contractId}/builder` as const

  const join = ({
    contractId,
    workspaceId,
  }: ContractBuilderRequestParams) => post<Response<App.Data.Contract.V2.ContractBuilderData>>({
    url: `${url(contractId)}/join`,
    workspaceId,
  })

  const refreshToken = ({
    contractId,
    workspaceId,
  }: ContractBuilderRequestParams) => post<Response<App.Data.Contract.V2.ContractBuilderParticipantData>>({
    url: `${url(contractId)}/refresh-token`,
    workspaceId,
  })

  const getSignatures = ({
    contractId,
    workspaceId,
  }: ContractBuilderRequestParams) => get<Response<SignatureParty[]>>({
    url: `${url(contractId)}/signatures`,
    workspaceId,
  })

  const getContractSize = ({
    contractId,
    workspaceId,
  }: ContractBuilderRequestParams) => get<Response<App.Data.Contract.V2.ContractSizeData>>({
    url: `${url(contractId)}/size`,
    workspaceId,
  })

  const addGuest = ({
    contractId,
    workspaceId,
    payload,
  }: {
    payload: GuestInvitationPayload
  } & ContractBuilderRequestParams) => post<Response<App.Data.Contract.V2.ContractBuilderGuestData>>({
    url: `${url(contractId)}/guests`,
    workspaceId,
    fetchOptions: { body: payload },
  })

  const getGuests = ({
    contractId,
    workspaceId,
  }: ContractBuilderRequestParams) => get<Response<App.Data.Contract.V2.ContractBuilderGuestData[]>>({
    url: `${url(contractId)}/guests`,
    workspaceId,
  })

  const deleteGuest = ({
    contractId,
    workspaceId,
    participantId,
  }: {
    participantId: string
  } & ContractBuilderRequestParams) => deleteRequest<void>({
    url: `${url(contractId)}/guests/${participantId}`,
    workspaceId,
  })

  const resendGuestInvitation = ({
    contractId,
    workspaceId,
    participantId,
    payload,
  }: {
    participantId: string
    payload: ResendGuestInvitationPayload
  } & ContractBuilderRequestParams) => post<Response<App.Data.Contract.V2.ContractBuilderGuestData>>({
    url: `${url(contractId)}/guests/${participantId}/resend`,
    workspaceId,
    fetchOptions: { body: payload },
  })

  const finalize = ({
    contractId,
    workspaceId,
    payload,
  }: {
    payload: FinalizePayload
  } & ContractBuilderRequestParams) => post<void>({
    url: `${url(contractId)}/finalize`,
    workspaceId,
    fetchOptions: { body: payload },
  })

  const redeemFreeContract = ({
    contractId,
    workspaceId,
  }: ContractBuilderRequestParams) => post<void>({
    url: `${url(contractId)}/use-free-contract`,
    workspaceId,
  })

  return {
    join,
    refreshToken,
    getSignatures,
    addGuest,
    finalize,
    redeemFreeContract,
    getContractSize,
    getGuests,
    resendGuestInvitation,
    deleteGuest,
  }
}
